import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Add, Delete, Refresh } from '@mui/icons-material';
import { reportCardAPI, academicAPI, studentAPI } from '../../services/api';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';

const extractCollection = (responseOrPayload) => {
  const payload = responseOrPayload?.data !== undefined ? responseOrPayload.data : responseOrPayload;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const extractSingleRecord = (responseOrPayload) => {
  const payload = responseOrPayload?.data !== undefined ? responseOrPayload.data : responseOrPayload;
  if (!payload) return null;
  if (Array.isArray(payload)) return payload[0] || null;
  if (payload?.data && !Array.isArray(payload.data)) return payload.data;
  return payload;
};

const getErrorMessage = (error, fallback = 'Operation failed') => (
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  fallback
);

const getAcademicYearFromTerm = (term) => (
  Number(term?.academic_year || term?.year || new Date().getFullYear())
);

const getTermName = (term) => term?.name || term?.term_name || 'Unknown Term';
const getSubjectName = (subject) => subject?.subject_name || subject?.name || 'Unknown Subject';
const getStudentName = (student) => (
  student?.full_name ||
  `${student?.last_name || ''} ${student?.first_name || ''}`.trim() ||
  student?.registration_no ||
  `Student #${student?.id}`
);
const getCommentText = (comment) => (
  comment?.comment_text ||
  comment?.comment ||
  ''
);

const INITIAL_SUBJECT_FORM = {
  student: null,
  term: null,
  subject: null,
  comment_text: '',
  commented_by: ''
};

const INITIAL_GENERAL_FORM = {
  student: null,
  term: null,
  class_teacher_comment: '',
  headteacher_comment: ''
};

const TemplatePicker = ({ type, gradeBoundaryId, conductRatingId, onSelect }) => {
  const [templates, setTemplates] = useState([]);
  const [value, setValue] = useState(null);

  useEffect(() => {
    const params = { comment_type: type };
    if (gradeBoundaryId) params.grade_boundary_id = gradeBoundaryId;
    if (conductRatingId) params.conduct_rating_id = conductRatingId;
    reportCardAPI.getCommentTemplates(params)
      .then((res) => setTemplates(Array.isArray(res.data) ? res.data : res.data?.data ?? []))
      .catch(() => {});
  }, [type, gradeBoundaryId, conductRatingId]);

  if (!templates.length) return null;

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={templates}
      value={value}
      onChange={(_, t) => { setValue(t); if (t) onSelect(t.comment_text); }}
      getOptionLabel={(t) => {
        const grade = t.grade_boundary ? ` [${t.grade_boundary.grade}]` : '';
        const conduct = t.conduct_rating ? ` [${t.conduct_rating.rating_name}]` : '';
        return `[${t.template_code}]${grade}${conduct} ${t.comment_text.slice(0, 50)}${t.comment_text.length > 50 ? '\u2026' : ''}`;
      }}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderInput={(params) => <TextField {...params} label="Use a template (optional)" margin="normal" />}
    />
  );
};

const responsiveAutocompleteSx = {
  width: '100%',
  minWidth: {
    sm: 240,
    lg: 280
  }
};

export default function CommentsManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, setTab] = useState(0);
  const [subjectComments, setSubjectComments] = useState([]);
  const [generalComments, setGeneralComments] = useState([]);
  const [terms, setTerms] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [loadingSetup, setLoadingSetup] = useState(true);
  const [loadingComments, setLoadingComments] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [subjectDialog, setSubjectDialog] = useState(false);
  const [generalDialog, setGeneralDialog] = useState(false);

  const [subjectForm, setSubjectForm] = useState(INITIAL_SUBJECT_FORM);
  const [generalForm, setGeneralForm] = useState(INITIAL_GENERAL_FORM);

  const [selectedTermFilter, setSelectedTermFilter] = useState(null);
  const [selectedStudentFilter, setSelectedStudentFilter] = useState(null);
  const [commentContext, setCommentContext] = useState(null); // { overall_boundary_id, subject_boundaries, conduct_boundary_id }

  // Fetch comment context (grade boundaries from marks + conduct) when student+term are set in a dialog
  const fetchCommentContext = useCallback(async (student, term) => {
    if (!student?.id || !term?.id) { setCommentContext(null); return; }
    try {
      const res = await reportCardAPI.getCommentContext(student.id, {
        academic_year: getAcademicYearFromTerm(term),
        term_id: term.id
      });
      setCommentContext(res.data);
    } catch { setCommentContext(null); }
  }, []);

  const fetchSetupData = useCallback(async () => {
    setLoadingSetup(true);
    try {
      const [termRes, subjectRes, studentRes] = await Promise.all([
        academicAPI.getTerms(),
        reportCardAPI.getActiveSubjects(),
        studentAPI.getActive({ per_page: 1000 })
      ]);

      setTerms(extractCollection(termRes));
      setSubjects(extractCollection(subjectRes));
      setStudents(extractCollection(studentRes));
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load setup data'));
    } finally {
      setLoadingSetup(false);
    }
  }, []);

  const loadComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      if (selectedStudentFilter?.id && selectedTermFilter?.id) {
        const params = {
          term_id: selectedTermFilter.id,
          academic_year: getAcademicYearFromTerm(selectedTermFilter)
        };

        if (tab === 0) {
          const response = await reportCardAPI.getSubjectCommentsForStudent(selectedStudentFilter.id, params);
          setSubjectComments(extractCollection(response));
        } else {
          const response = await reportCardAPI.getGeneralCommentForStudent(selectedStudentFilter.id, params);
          const record = extractSingleRecord(response);
          setGeneralComments(record ? [record] : []);
        }
        return;
      }

      if (tab === 0) {
        const params = { per_page: 1000 };
        if (selectedTermFilter?.id) {
          params.term_id = selectedTermFilter.id;
          params.academic_year = getAcademicYearFromTerm(selectedTermFilter);
        }
        if (selectedStudentFilter?.id) params.student_id = selectedStudentFilter.id;
        const response = await reportCardAPI.getSubjectComments(params);
        setSubjectComments(extractCollection(response));
      } else {
        const params = { per_page: 1000 };
        if (selectedTermFilter?.id) {
          params.term_id = selectedTermFilter.id;
          params.academic_year = getAcademicYearFromTerm(selectedTermFilter);
        }
        if (selectedStudentFilter?.id) params.student_id = selectedStudentFilter.id;
        const response = await reportCardAPI.getGeneralComments(params);
        setGeneralComments(extractCollection(response));
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load comments'));
      if (tab === 0) setSubjectComments([]);
      else setGeneralComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, [selectedStudentFilter, selectedTermFilter, tab]);

  useEffect(() => {
    fetchSetupData();
  }, [fetchSetupData]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const currentComments = tab === 0 ? subjectComments : generalComments;

  const filteredComments = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return currentComments;

    return currentComments.filter((comment) => {
      const student = getStudentName(comment?.student).toLowerCase();
      const term = getTermName(comment?.term).toLowerCase();
      const subject = getSubjectName(comment?.subject).toLowerCase();
      const commentText = getCommentText(comment).toLowerCase();
      const classTeacherComment = String(comment?.class_teacher_comment || '').toLowerCase();
      const headteacherComment = String(comment?.headteacher_comment || '').toLowerCase();
      const commenter = String(comment?.teacher?.full_name || comment?.commented_by || '').toLowerCase();

      return (
        student.includes(normalizedSearch) ||
        term.includes(normalizedSearch) ||
        subject.includes(normalizedSearch) ||
        commentText.includes(normalizedSearch) ||
        classTeacherComment.includes(normalizedSearch) ||
        headteacherComment.includes(normalizedSearch) ||
        commenter.includes(normalizedSearch)
      );
    });
  }, [currentComments, searchTerm]);

  const handleCreateSubjectComment = async () => {
    const commentText = subjectForm.comment_text.trim();
    if (!subjectForm.student?.id || !subjectForm.term?.id || !subjectForm.subject?.id || !commentText) {
      setError('Student, term, subject, and comment are required');
      return;
    }

    try {
      await reportCardAPI.createSubjectComment({
        student_id: subjectForm.student.id,
        subject_id: subjectForm.subject.id,
        academic_year: getAcademicYearFromTerm(subjectForm.term),
        term_id: subjectForm.term.id,
        comment_text: commentText,
        comment: commentText,
        commented_by: subjectForm.commented_by || null
      });

      setSuccess('Subject comment created successfully');
      setSubjectDialog(false);
      setSubjectForm(INITIAL_SUBJECT_FORM);
      await loadComments();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create comment'));
    }
  };

  const handleCreateGeneralComment = async () => {
    const classTeacherComment = generalForm.class_teacher_comment.trim();
    const headteacherComment = generalForm.headteacher_comment.trim();

    if (!generalForm.student?.id || !generalForm.term?.id || (!classTeacherComment && !headteacherComment)) {
      setError('Student, term, and at least one comment are required');
      return;
    }

    try {
      await reportCardAPI.createGeneralComment({
        student_id: generalForm.student.id,
        academic_year: getAcademicYearFromTerm(generalForm.term),
        term_id: generalForm.term.id,
        class_teacher_comment: classTeacherComment || null,
        headteacher_comment: headteacherComment || null
      });

      setSuccess('General comment created successfully');
      setGeneralDialog(false);
      setGeneralForm(INITIAL_GENERAL_FORM);
      await loadComments();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create comment'));
    }
  };

  const handleDeleteSubjectComment = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await reportCardAPI.deleteSubjectComment(id);
      setSuccess('Comment deleted successfully');
      await loadComments();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete comment'));
    }
  };

  const handleDeleteGeneralComment = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await reportCardAPI.deleteGeneralComment(id);
      setSuccess('Comment deleted successfully');
      await loadComments();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete comment'));
    }
  };

  return (
    <Box>
      <ModulePageHeader
        title="Comments Management"
        subtitle="Manage subject and general comments with searchable filters"
        actions={[
          <Button
            key="refresh"
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchSetupData();
              loadComments();
            }}
            disabled={loadingSetup || loadingComments}
            fullWidth={isMobile}
          >
            Refresh
          </Button>,
          <Button
            key="add-comment"
            variant="contained"
            startIcon={<Add />}
            onClick={() => (tab === 0 ? setSubjectDialog(true) : setGeneralDialog(true))}
            fullWidth={isMobile}
          >
            Add Comment
          </Button>
        ]}
      />

      {error ? <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert> : null}
      {success ? <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert> : null}

      <Card>
        <CardContent>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
            <Tab label="Subject Comments" />
            <Tab label="General Comments" />
          </Tabs>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                sx={responsiveAutocompleteSx}
                options={terms}
                value={selectedTermFilter}
                onChange={(_, value) => setSelectedTermFilter(value)}
                getOptionLabel={(option) => getTermName(option)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Filter by Term" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                sx={responsiveAutocompleteSx}
                options={students}
                value={selectedStudentFilter}
                onChange={(_, value) => setSelectedStudentFilter(value)}
                getOptionLabel={(option) => getStudentName(option)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Filter by Student" size="small" />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search Comments"
                placeholder="Search comment text, student, term, subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
          </Grid>

          {loadingComments ? (
            <Box py={4} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : filteredComments.length === 0 ? (
            <EmptyRecordsState message="No comments found for selected filters." />
          ) : isMobile ? (
            <ResponsiveRecordList
              items={filteredComments}
              renderItem={(comment) => (
                <Stack spacing={1}>
                  <Typography fontWeight={700}>{getStudentName(comment?.student)}</Typography>
                  <Typography variant="body2" color="text.secondary">{getTermName(comment?.term)}</Typography>
                  {tab === 0 ? (
                    <Typography variant="body2" color="text.secondary">{getSubjectName(comment?.subject)}</Typography>
                  ) : null}
                  {tab === 0 ? (
                    <Typography variant="body2" color="text.secondary">{getCommentText(comment)}</Typography>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Class Teacher: {comment?.class_teacher_comment || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Headteacher: {comment?.headteacher_comment || '-'}
                      </Typography>
                    </>
                  )}
                  <Box textAlign="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => (tab === 0 ? handleDeleteSubjectComment(comment.id) : handleDeleteGeneralComment(comment.id))}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Stack>
              )}
            />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell>Term</TableCell>
                    {tab === 0 ? <TableCell>Subject</TableCell> : null}
                    {tab === 0 ? <TableCell>Comment</TableCell> : <TableCell>Class Teacher Comment</TableCell>}
                    {tab === 1 ? <TableCell>Headteacher Comment</TableCell> : null}
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredComments.map((comment) => (
                    <TableRow key={comment.id}>
                      <TableCell>{getStudentName(comment?.student)}</TableCell>
                      <TableCell>{getTermName(comment?.term)}</TableCell>
                      {tab === 0 ? <TableCell>{getSubjectName(comment?.subject)}</TableCell> : null}
                      {tab === 0 ? (
                        <TableCell>{getCommentText(comment)}</TableCell>
                      ) : (
                        <TableCell>{comment?.class_teacher_comment || '-'}</TableCell>
                      )}
                      {tab === 1 ? <TableCell>{comment?.headteacher_comment || '-'}</TableCell> : null}
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => (tab === 0 ? handleDeleteSubjectComment(comment.id) : handleDeleteGeneralComment(comment.id))}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={subjectDialog} onClose={() => setSubjectDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add Subject Comment</DialogTitle>
        <DialogContent>
          <Autocomplete
            fullWidth
            sx={responsiveAutocompleteSx}
            options={students}
            value={subjectForm.student}
            onChange={(_, value) => {
              setSubjectForm({ ...subjectForm, student: value });
              fetchCommentContext(value, subjectForm.term);
            }}
            getOptionLabel={(option) => getStudentName(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Student" margin="normal" size="small" required />}
          />
          <Autocomplete
            fullWidth
            sx={responsiveAutocompleteSx}
            options={terms}
            value={subjectForm.term}
            onChange={(_, value) => {
              setSubjectForm({ ...subjectForm, term: value });
              fetchCommentContext(subjectForm.student, value);
            }}
            getOptionLabel={(option) => getTermName(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Term" margin="normal" size="small" required />}
          />
          <Autocomplete
            fullWidth
            sx={responsiveAutocompleteSx}
            options={subjects}
            value={subjectForm.subject}
            onChange={(_, value) => setSubjectForm({ ...subjectForm, subject: value })}
            getOptionLabel={(option) => getSubjectName(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Subject" margin="normal" size="small" required />}
          />
          <TemplatePicker
            type="Subject"
            gradeBoundaryId={commentContext?.subject_boundaries?.[subjectForm.subject?.id]?.grade_boundary_id ?? null}
            onSelect={(text) => setSubjectForm((f) => ({ ...f, comment_text: text }))}
          />
          <TextField
            fullWidth
            label="Comment"
            value={subjectForm.comment_text}
            onChange={(e) => setSubjectForm({ ...subjectForm, comment_text: e.target.value })}
            margin="normal"
            multiline
            rows={4}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubjectDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateSubjectComment} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={generalDialog} onClose={() => setGeneralDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add General Comment</DialogTitle>
        <DialogContent>
          <Autocomplete
            fullWidth
            sx={responsiveAutocompleteSx}
            options={students}
            value={generalForm.student}
            onChange={(_, value) => {
              setGeneralForm({ ...generalForm, student: value });
              fetchCommentContext(value, generalForm.term);
            }}
            getOptionLabel={(option) => getStudentName(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Student" margin="normal" size="small" required />}
          />
          <Autocomplete
            fullWidth
            sx={responsiveAutocompleteSx}
            options={terms}
            value={generalForm.term}
            onChange={(_, value) => {
              setGeneralForm({ ...generalForm, term: value });
              fetchCommentContext(generalForm.student, value);
            }}
            getOptionLabel={(option) => getTermName(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Term" margin="normal" size="small" required />}
          />
          <TemplatePicker
            type="Class Teacher"
            gradeBoundaryId={commentContext?.overall_boundary_id ?? null}
            onSelect={(text) => setGeneralForm((f) => ({ ...f, class_teacher_comment: text }))}
          />
          <TextField
            fullWidth
            label="Class Teacher Comment"
            value={generalForm.class_teacher_comment}
            onChange={(e) => setGeneralForm({ ...generalForm, class_teacher_comment: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <TemplatePicker
            type="Headteacher"
            conductRatingId={commentContext?.conduct_rating_id ?? null}
            gradeBoundaryId={commentContext?.overall_boundary_id ?? null}
            onSelect={(text) => setGeneralForm((f) => ({ ...f, headteacher_comment: text }))}
          />
          <TextField
            fullWidth
            label="Headteacher Comment"
            value={generalForm.headteacher_comment}
            onChange={(e) => setGeneralForm({ ...generalForm, headteacher_comment: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGeneralDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateGeneralComment} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
